<template>
  <div>
    <v-overlay
      v-bind:value="!!value"
      z-index="200"
      v-on:click.native="visible = false"
    />
    <transition name="slide-right">
      <v-card
        v-if="!!value"
        class="item-edit-panel"
        elevation="16"
        tile
      >
        <!-- Header -->
        <div class="item-edit-panel__header">
          <v-icon small class="mr-2">mdi-monitor</v-icon>
          <span class="item-edit-panel__title">{{ isNew ? 'New Device' : 'Edit Device' }}</span>

          <v-chip
            v-if="!isNew && lastSeen"
            x-small
            v-bind:color="lastSeen.online ? 'success' : 'error'"
            text-color="white"
            class="ml-2"
          >
            {{ lastSeen.online ? 'Online' : 'Offline' }}
          </v-chip>

          <v-spacer />
          <v-btn icon small v-on:click="visible = false">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-divider />

        <!-- Body -->
        <div class="item-edit-panel__body">
          <v-form ref="editForm" lazy-validation>

            <div class="panel-section">Identification</div>

            <div class="d-flex align-center">
              <v-text-field
                v-model="item.name"
                v-bind:rules="[rules.mandatory]"
                label="Name"
                outlined dense
              />
              <v-switch
                v-model="item.enabled"
                label="Enabled"
                class="ml-4 mt-0"
              />
            </div>

            <v-row dense>
              <v-col cols="6">
                <v-text-field
                  v-model="item.mac_address"
                  v-bind:rules="[rules.macAddress]"
                  v-bind:readonly="!isNew"
                  v-bind:hint="!isNew ? '' : 'Leave empty to auto-generate'"
                  label="MAC Address"
                  persistent-hint
                  outlined dense
                  class="mono-input"
                />
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-if="isAdmin"
                  v-model="item.wg_allowed_ip"
                  v-bind:readonly="!isNew"
                  v-bind:rules="[rules.ipAddress]"
                  label="WireGuard IP"
                  outlined dense
                  class="mono-input"
                  v-bind:append-icon="item.wg_allowed_ip && !isNew ? 'mdi-content-copy' : undefined"
                  v-on:click:append="clipboard_copy(item.wg_allowed_ip, 'IP copied')"
                />
                <v-text-field
                  v-else-if="!isNew"
                  v-bind:value="item.wg_allowed_ip"
                  label="WireGuard IP"
                  outlined dense readonly
                  class="mono-input"
                  append-icon="mdi-content-copy"
                  v-on:click:append="clipboard_copy(item.wg_allowed_ip, 'IP copied')"
                />
                <div
                  v-else
                  class="caption text--secondary pa-2 mt-1"
                  style="border:1px dashed rgba(128,128,128,.3);border-radius:4px"
                >
                  <v-icon x-small>mdi-auto-fix</v-icon>
                  IP auto-assigned
                </div>
              </v-col>
            </v-row>

            <v-divider class="my-3" />
            <div class="panel-section">Assignment</div>

            <v-autocomplete
              v-model="item.group_id"
              v-bind:items="groupItems"
              item-text="label"
              item-value="value"
              v-bind:rules="isAdmin ? [] : [rules.mandatory]"
              label="Group"
              outlined dense
              v-bind:clearable="isAdmin"
              prepend-inner-icon="mdi-account-group"
            />

            <v-text-field
              v-model="item.customer"
              label="Customer"
              outlined dense
              prepend-inner-icon="mdi-domain"
            />

            <v-divider class="my-3" />
            <div class="panel-section">Hardware</div>

            <v-row dense>
              <v-col cols="6">
                <v-text-field
                  v-model="item.sim"
                  v-bind:rules="[rules.sim]"
                  label="SIM number"
                  outlined dense
                  class="mono-input"
                />
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="item.imei"
                  v-bind:rules="[rules.imei]"
                  label="IMEI"
                  outlined dense
                  class="mono-input"
                />
              </v-col>
            </v-row>

            <v-divider class="my-3" />
            <div class="panel-section">Advanced</div>

            <v-row dense>
              <v-col cols="8">
                <v-text-field
                  v-model="item.ssh_username"
                  label="Default SSH Username"
                  outlined dense
                  prepend-inner-icon="mdi-account-key-outline"
                />
              </v-col>
              <v-col cols="4">
                <v-text-field
                  v-model.number="item.ssh_port"
                  label="SSH Port"
                  outlined dense
                  type="number"
                  placeholder="22"
                />
              </v-col>
            </v-row>

            <v-textarea
              v-model="item.notes"
              label="Notes"
              outlined dense auto-grow
              rows="2"
            />

            <!-- Stats section — visible only for existing boxes -->
            <template v-if="!isNew">
              <v-divider class="my-3" />
              <div class="d-flex align-center mb-3">
                <div class="panel-section mb-0">Connection Stats</div>
                <v-spacer />
                <v-btn icon x-small v-on:click="loadBoxStats">
                  <v-icon x-small>mdi-refresh</v-icon>
                </v-btn>
              </div>

              <!-- Empty state -->
              <div
                v-if="!boxStats"
                class="text-center py-4 text--disabled caption"
              >
                <v-icon small class="d-block mb-1">mdi-chart-bar</v-icon>
                No activity recorded
              </div>

              <!-- Stats content -->
              <template v-else>
                <v-row dense class="mb-2">
                  <v-col cols="12">
                    <div class="stats-row">
                      <span class="stats-label">Endpoint IP</span>
                      <span class="mono caption">{{ boxStats.endpoint_ip || '—' }}</span>
                    </div>
                  </v-col>
                  <v-col cols="6">
                    <div class="stats-row">
                      <span class="stats-label">Total RX</span>
                      <span>{{ format_bytes(boxStats.rx_total) }}</span>
                    </div>
                  </v-col>
                  <v-col cols="6">
                    <div class="stats-row">
                      <span class="stats-label">Total TX</span>
                      <span>{{ format_bytes(boxStats.tx_total) }}</span>
                    </div>
                  </v-col>
                  <v-col cols="6">
                    <div class="stats-row">
                      <span class="stats-label">Uptime 24h</span>
                      <span>{{ uptimePercent(boxStats.uptime_24h) }}</span>
                    </div>
                  </v-col>
                  <v-col cols="6">
                    <div class="stats-row">
                      <span class="stats-label">Uptime 7d</span>
                      <span>{{ uptimePercent(boxStats.uptime_7d) }}</span>
                    </div>
                  </v-col>
                </v-row>

                <!-- Recent events -->
                <div v-if="boxStats.recent_events && boxStats.recent_events.length">
                  <div class="panel-section mb-2">Recent Events</div>
                  <div
                    v-for="(event, idx) in boxStats.recent_events"
                    v-bind:key="idx"
                    class="event-row"
                  >
                    <v-chip
                      x-small
                      v-bind:color="eventTypeColor(event.type)"
                      dark
                      class="mr-2"
                    >{{ event.type }}</v-chip>
                    <span class="mono caption mr-2">{{ event.endpoint_ip }}</span>
                    <span class="caption text--secondary">{{ format_relative(event.created) }}</span>
                  </div>
                </div>
              </template>
            </template>

          </v-form>
        </div>

        <v-divider />

        <!-- Footer -->
        <div class="item-edit-panel__footer">
          <v-menu v-if="!isNew" top offset-y>
            <template v-slot:activator="{ on }">
              <v-btn color="error" text small v-on="on">
                <v-icon left small>mdi-delete-outline</v-icon>Delete
              </v-btn>
            </template>
            <v-btn color="error" depressed small v-on:click="$emit('delete', item)">
              <v-icon left small>mdi-delete</v-icon>Confirm delete
            </v-btn>
          </v-menu>
          <v-spacer />
          <v-btn v-if="!isNew" text small v-on:click="$emit('ssh', item)">
            <v-icon left small>mdi-console</v-icon>SSH
          </v-btn>
          <v-btn text small v-on:click="$emit('wg-config', item)">
            <v-icon left small>mdi-shield-key-outline</v-icon>WG Config
          </v-btn>
          <v-btn color="primary" depressed small v-on:click="save">
            <v-icon left small>mdi-content-save-outline</v-icon>
            {{ isNew ? 'Create' : 'Save' }}
          </v-btn>
        </div>
      </v-card>
    </transition>
  </div>
</template>

<script>
export default {
  name: "ItemEdit",

  props: {
    value:      { type: Object, default: null },
    groups:     { type: Array,  default: () => [] },
    isAdmin:    { type: Boolean, default: false },
    lastSeenMap:{ type: Object, default: () => ({}) }
  },

  data: () => ({
    isNew:    false,
    boxStats: null,
    rules: {
      mandatory:  value => !!value || "Mandatory field",
      macAddress: value =>
        !value || /^([0-9A-Fa-f]{2}[:]){5}([0-9A-Fa-f]{2})$/.test(value) || "Invalid value",
      sim:        value =>
        !value || /^[0-9]{20}$/.test(value) || "Must be 20 digits",
      imei:       value =>
        !value || /^[0-9]{15}$/.test(value) || "Must be 15 digits",
      ipAddress:  value =>
        !value ||
        /^((?:[0-9]|[1-9][0-9]|1[0-9][0-9]|2[0-4][0-9]|25[0-5])[.]){3}(?:[0-9]|[1-9][0-9]|1[0-9][0-9]|2[0-4][0-9]|25[0-5])$/.test(value) ||
        "Not a valid IP"
    }
  }),

  computed: {
    visible: {
      get()      { return !!this.value; },
      set(value) { value === false && this.$emit("input", null); }
    },

    item() { return this.value || {}; },

    groupItems() {
      return this.groups.map(g => ({
        value: g.id,
        label: g.is_servers ? `${g.name} (view all devices)` : g.name
      }));
    },

    lastSeen() {
      return this.item.wg_public_key
        ? this.lastSeenMap[this.item.wg_public_key]
        : null;
    },

  },

  watch: {
    visible(val) {
      this.isNew = val && !("mac_address" in this.item);
      this.$refs.editForm && this.$refs.editForm.resetValidation();
      if (val && !this.isNew) {
        this.loadBoxStats();
      } else {
        this.boxStats = null;
      }
    },

    lastSeenMap() {
      if (!this.isNew && this.visible) {
        this.loadBoxStats();
      }
    }
  },

  methods: {
    isOnline(seen) {
      return seen && seen.seconds < 900;
    },

    save() {
      this.$refs.editForm.validate() && this.$emit("save", this.item);
    },

    loadBoxStats() {
      this.$server
        .query({ url: `stats/box/${this.item.mac_address}` })
        .then(stats  => { this.boxStats = stats; })
        .catch(() => { this.boxStats = null; });
    },

    /** @param {"connect"|"disconnect"|"ip_change"} type */
    eventTypeColor(type) {
      const colors = { connect: "success", disconnect: "error", ip_change: "warning" };
      return colors[type] || "grey";
    },

    /** @param {number|null} ratio - 0..1 fraction */
    uptimePercent(ratio) {
      if (ratio == null) return "—";
      return `${Math.round(ratio * 100)}%`;
    }
  }
};
</script>

<style scoped>
.item-edit-panel {
  position: fixed;
  top: 0; right: 0; bottom: 0;
  width: 600px;
  z-index: 201;
  border-radius: 0 !important;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.item-edit-panel__header {
  display: flex; align-items: center;
  padding: 14px 16px; flex-shrink: 0;
}
.item-edit-panel__title { font-size: 15px; font-weight: 700; }
.item-edit-panel__body {
  flex: 1; overflow-y: auto;
  padding: 16px 18px;
}
.item-edit-panel__footer {
  display: flex; align-items: center; gap: 8px;
  padding: 12px 16px; flex-shrink: 0;
}

.panel-section {
  font-size: 11px; font-weight: 700; letter-spacing: .06em;
  text-transform: uppercase; opacity: .5;
  margin-bottom: 12px;
}

.mono-input >>> input { font-family: 'JetBrains Mono', 'Fira Code', monospace !important; font-size: 12px !important; }

.stats-row {
  display: flex;
  flex-direction: column;
  padding: 4px 0;
}
.stats-label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: .05em;
  text-transform: uppercase;
  opacity: .45;
}

.event-row {
  display: flex;
  align-items: center;
  padding: 3px 0;
  border-bottom: 1px solid rgba(128,128,128,.08);
}
.event-row:last-child { border-bottom: none; }

/* Slide animation */
.slide-right-enter-active,
.slide-right-leave-active { transition: transform .25s ease; }
.slide-right-enter,
.slide-right-leave-to     { transform: translateX(100%); }
</style>
